/**
 * QoC Assessment Excel Export
 * Mirrors the Phocweni Excel tool structure exactly:
 * - Cover Page sheet
 * - One sheet per clause (AACC, COP, MOM, ...)
 */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

const CLAUSES = [
    { code:'AACC', title:'Access, Assessment and Continuity of Care',           color:'FF0EA5E9', num:1  },
    { code:'COP',  title:'Care of the Patient',                                  color:'FF8B5CF6', num:2  },
    { code:'MOM',  title:'Management of Medication',                             color:'FFF59E0B', num:3  },
    { code:'PRPE', title:"Patient Rights and Patient Education",                  color:'FF10B981', num:4  },
    { code:'IPCM', title:'Infection Prevention and Control Management',           color:'FFEF4444', num:5  },
    { code:'PSQI', title:'Patient Safety and Quality Improvement',               color:'FF3B82F6', num:6  },
    { code:'GROM', title:'Governance, Roles and Responsibilities of Management', color:'FF6366F1', num:7  },
    { code:'FMS',  title:'Facility Management, Safety and Support Services',     color:'FFEC4899', num:8  },
    { code:'HRE',  title:'Human Resource and Education',                         color:'FF14B8A6', num:9  },
    { code:'ISM',  title:'Information System Management',                        color:'FFA855F7', num:10 },
];

const RATING = [
    { min:98,  label:'Outstanding'          },
    { min:95,  label:'Excellent'            },
    { min:90,  label:'Good'                 },
    { min:85,  label:'Fair'                 },
    { min:70,  label:'Requires Improvement' },
    { min:0,   label:'Inadequate'           },
];
const getRatingLabel = s => (RATING.find(r => s >= r.min) || RATING[RATING.length-1]).label;

function applyHeaderStyle(cell, bgHex, fontSize=10, bold=true) {
    cell.fill   = { type:'pattern', pattern:'solid', fgColor:{ argb: bgHex } };
    cell.font   = { bold, color:{ argb:'FFFFFFFF' }, size: fontSize };
    cell.alignment = { vertical:'middle', horizontal:'center', wrapText:true };
    cell.border = {
        top:{ style:'thin' }, bottom:{ style:'thin' },
        left:{ style:'thin' }, right:{ style:'thin' }
    };
}
function applyCellStyle(cell, bgHex='FFFFFFFF', color='FF000000', bold=false, wrap=true) {
    cell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb: bgHex } };
    cell.font = { bold, color:{ argb: color }, size:9 };
    cell.alignment = { vertical:'middle', wrapText: wrap };
    cell.border = {
        top:{ style:'thin', color:{ argb:'FFD0D0D0' } },
        bottom:{ style:'thin', color:{ argb:'FFD0D0D0' } },
        left:{ style:'thin', color:{ argb:'FFD0D0D0' } },
        right:{ style:'thin', color:{ argb:'FFD0D0D0' } },
    };
}

export async function exportFacilityAssessmentExcel({ facility, assessment, clauseScores, responses }) {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'QoC Platform';
    wb.created = new Date();

    // Build lookup maps
    const clauseMap = {};
    clauseScores.forEach(s => { clauseMap[s.clause_code] = s; });
    const meByClause = {};
    responses.forEach(r => {
        if (!meByClause[r.clause_code]) meByClause[r.clause_code] = [];
        meByClause[r.clause_code].push(r);
    });

    // ══════════════════════════════════════════════════════════
    // SHEET 1 — COVER PAGE
    // ══════════════════════════════════════════════════════════
    const cover = wb.addWorksheet('Cover Page', {
        pageSetup: { paperSize:9, orientation:'portrait', fitToPage:true }
    });
    cover.columns = [
        { width:22 }, { width:30 }, { width:22 }, { width:30 },
    ];

    // Title
    cover.mergeCells('A1:D1');
    const titleCell = cover.getCell('A1');
    titleCell.value = 'Eswatini Ministry of Health';
    titleCell.font = { bold:true, size:16, color:{ argb:'FF1890FF' } };
    titleCell.alignment = { horizontal:'center', vertical:'middle' };
    titleCell.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF0F172A' } };
    cover.getRow(1).height = 32;

    cover.mergeCells('A2:D2');
    const subTitle = cover.getCell('A2');
    subTitle.value = 'Eswatini Quality of Care (QoC) Standards Audit Report';
    subTitle.font = { bold:true, size:12, color:{ argb:'FFFFFFFF' } };
    subTitle.alignment = { horizontal:'center', vertical:'middle' };
    subTitle.fill = { type:'pattern', pattern:'solid', fgColor:{ argb:'FF0F172A' } };
    cover.getRow(2).height = 22;

    cover.addRow([]);

    // Facility info
    const infoRows = [
        ['Facility Name',    facility.business_name,    'Audit Date',    moment(assessment.assessment_date).format('DD MMMM YYYY')],
        ['Type of Facility', facility.site_type||'N/A', 'RC Number',     facility.site_code||'N/A'],
        ['Facility Address', facility.address||'N/A',   'Facility Code', facility.fac_code||'N/A'],
        ['Region',           facility.located_region||'N/A', 'Assessor', assessment.assessor_name||'N/A'],
    ];
    infoRows.forEach(row => {
        const r = cover.addRow(row);
        r.height = 20;
        applyCellStyle(r.getCell(1), 'FFF0F5FF', 'FF475569', true);
        applyCellStyle(r.getCell(2), 'FFFAFBFF', 'FF0F172A', false);
        applyCellStyle(r.getCell(3), 'FFF0F5FF', 'FF475569', true);
        applyCellStyle(r.getCell(4), 'FFFAFBFF', 'FF0F172A', false);
    });

    cover.addRow([]);

    // Overall score banner
    const overallPct = parseFloat(assessment.overall_percentage || 0);
    const ratingLabel = getRatingLabel(overallPct);
    cover.mergeCells(`A${cover.rowCount+1}:D${cover.rowCount+1}`);
    const scoreRow = cover.lastRow;
    scoreRow.getCell(1).value = `Overall Score: ${overallPct.toFixed(1)}% — ${ratingLabel}`;
    scoreRow.getCell(1).font = { bold:true, size:14, color:{ argb:'FFFFFFFF' } };
    scoreRow.getCell(1).alignment = { horizontal:'center', vertical:'middle' };
    scoreRow.getCell(1).fill = { type:'pattern', pattern:'solid',
        fgColor:{ argb: overallPct>=90?'FF389E0D':overallPct>=70?'FFFA8C16':'FFEF4444' } };
    scoreRow.height = 28;

    cover.addRow([]);

    // Section scores table header
    const hdr = cover.addRow(['#', 'Clause', 'Score (%)', 'Met', 'Partial', 'Not Met', 'NA/NC', 'Rating']);
    hdr.height = 20;
    ['A','B','C','D','E','F','G','H'].forEach((col,i) => {
        applyHeaderStyle(cover.getCell(`${col}${hdr.number}`), 'FF0F172A', 10);
    });
    cover.getColumn('A').width = 4;
    cover.getColumn('B').width = 45;
    cover.getColumn('C').width = 12;
    cover.getColumn('D').width = 8;
    cover.getColumn('E').width = 10;
    cover.getColumn('F').width = 10;
    cover.getColumn('G').width = 10;
    cover.getColumn('H').width = 22;

    CLAUSES.forEach(cl => {
        const s = clauseMap[cl.code];
        const pct = s ? parseFloat(s.total_score||0) : null;
        const rating = pct !== null ? getRatingLabel(pct) : 'Not assessed';
        const bgColor = pct === null ? 'FFF5F5F5'
            : pct >= 90 ? 'FFF6FFED'
            : pct >= 70 ? 'FFFFF7E6'
            : 'FFFFF2F0';

        const r = cover.addRow([
            cl.num,
            `${cl.code} — ${cl.title}`,
            pct !== null ? parseFloat(pct.toFixed(1)) : '—',
            s?.met_count || '—',
            s?.partially_met_count || '—',
            s?.not_met_count || '—',
            s ? `${s.not_applicable_count||0}/${s.nc_count||0}` : '—',
            rating,
        ]);
        r.height = 18;
        r.eachCell(cell => {
            applyCellStyle(cell, bgColor);
            cell.alignment = { horizontal:'center', vertical:'middle' };
        });
        r.getCell(2).alignment = { horizontal:'left', vertical:'middle' };
        r.getCell(2).font = { bold:true, color:{ argb:'FF'+cl.color.slice(2) }, size:9 };
        if (pct !== null) {
            r.getCell(3).font = { bold:true,
                color:{ argb: pct>=90?'FF389E0D':pct>=70?'FFFA8C16':'FFEF4444' }, size:10 };
        }
    });

    // ══════════════════════════════════════════════════════════
    // SHEETS 2–11 — ONE PER CLAUSE
    // ══════════════════════════════════════════════════════════
    CLAUSES.forEach(cl => {
        const ws = wb.addWorksheet(cl.code, {
            pageSetup: { paperSize:9, orientation:'landscape', fitToPage:true }
        });

        const score = clauseMap[cl.code];
        const mes   = meByClause[cl.code] || [];
        const pct   = score ? parseFloat(score.total_score||0) : null;
        const A     = score?.total_audited || 0;
        const B     = A > 0 ? (100/A) : 0;

        // Sheet header
        ws.mergeCells('A1:I1');
        ws.getCell('A1').value = `Standard ${cl.num}. ${cl.title} (${cl.code})`;
        ws.getCell('A1').font = { bold:true, size:13, color:{argb:'FFFFFFFF'} };
        ws.getCell('A1').fill = { type:'pattern', pattern:'solid', fgColor:{argb: cl.color} };
        ws.getCell('A1').alignment = { horizontal:'left', vertical:'middle' };
        ws.getRow(1).height = 28;

        // Facility + date sub-header
        ws.mergeCells('A2:D2');
        ws.getCell('A2').value = `${facility.business_name}  •  Audit Date: ${moment(assessment.assessment_date).format('DD MMMM YYYY')}`;
        ws.getCell('A2').font = { size:10, color:{argb:'FF475569'} };
        ws.mergeCells('E2:I2');
        if (pct !== null) {
            ws.getCell('E2').value = `Score: ${pct.toFixed(2)}%  —  ${getRatingLabel(pct)}`;
            ws.getCell('E2').font = { bold:true, size:11,
                color:{argb: pct>=90?'FF389E0D':pct>=70?'FFFA8C16':'FFEF4444'} };
            ws.getCell('E2').alignment = { horizontal:'right' };
        }
        ws.getRow(2).height = 18;

        // Scoring summary row
        ws.addRow([]);
        const summRow = ws.addRow([
            `A (Audited MEs) = ${A}`,
            `B = 100÷${A} = ${B.toFixed(2)}`,
            `C (Met) = ${score?.met_count||0}`,
            `D (Partial) = ${score?.partially_met_count||0}`,
            `E (Not Met) = ${score?.not_met_count||0}`,
            `NA = ${score?.not_applicable_count||0}`,
            `NC = ${score?.nc_count||0}`,
            `Total = ${pct !== null ? pct.toFixed(2)+'%' : '—'}`,
            `Rating: ${pct !== null ? getRatingLabel(pct) : 'Not assessed'}`,
        ]);
        summRow.eachCell(cell => {
            cell.fill = { type:'pattern', pattern:'solid', fgColor:{argb:'FFE8F4FF'} };
            cell.font = { size:9, bold:true, color:{argb:'FF0F172A'} };
            cell.border = { bottom:{style:'medium', color:{argb: cl.color}} };
        });
        summRow.height = 16;
        ws.addRow([]);

        // Column widths
        ws.getColumn(1).width = 12;  // Ref No
        ws.getColumn(2).width = 48;  // ME Text
        ws.getColumn(3).width = 16;  // Fulfillment
        ws.getColumn(4).width = 28;  // Comment
        ws.getColumn(5).width = 28;  // General Observation
        ws.getColumn(6).width = 14;  // Criticality
        ws.getColumn(7).width = 28;  // Area of Good Practice
        ws.getColumn(8).width = 28;  // Evidence Notes
        ws.getColumn(9).width = 10;  // KCS flag

        // ME table header
        const meHdr = ws.addRow([
            'Ref No', 'Measurable Element', 'Fulfillment',
            'Comment', 'General Observation', 'Criticality of Observation',
            'Area of Good Practice', 'Evidence Notes', 'KCS'
        ]);
        meHdr.height = 20;
        meHdr.eachCell(cell => applyHeaderStyle(cell, cl.color, 9));

        // ME data rows
        if (mes.length === 0) {
            const emptyRow = ws.addRow(['No responses recorded for this clause', '', '', '', '', '', '', '', '']);
            ws.mergeCells(`A${emptyRow.number}:I${emptyRow.number}`);
            emptyRow.getCell(1).font = { italic:true, color:{argb:'FF888888'}, size:9 };
        } else {
            mes.forEach((me, idx) => {
                const isKCS = parseInt(me.is_critical) === 1;
                const f = me.fulfillment || '';
                const bgColor = f === 'Not Met' ? 'FFFFF2F0'
                    : f === 'Partially Met' ? 'FFFFF7E6'
                    : f === 'Met' ? 'FFF6FFED'
                    : idx % 2 === 0 ? 'FFFAFBFF' : 'FFFFFFFF';
                const fColor = f === 'Met' ? 'FF389E0D'
                    : f === 'Partially Met' ? 'FFFA8C16'
                    : f === 'Not Met' ? 'FFEF4444'
                    : f === 'Not Applicable' ? 'FF595959'
                    : 'FF722ED1';

                const row = ws.addRow([
                    me.ref_no || '',
                    me.me_text || me.question_text || '',
                    f,
                    me.comment || '',
                    me.general_observation || '',
                    me.criticality_of_observation || '',
                    me.area_of_good_practice || '',
                    me.evidence_notes || '',
                    isKCS ? '⚠ KCS' : '',
                ]);
                row.height = 45;
                row.eachCell(cell => {
                    applyCellStyle(cell, bgColor);
                });
                // Ref No styling
                row.getCell(1).font = { bold:true,
                    color:{ argb: isKCS ? 'FFEF4444' : 'FF'+cl.color.slice(2) }, size:9 };
                // Fulfillment color
                row.getCell(3).font = { bold:true, color:{argb: fColor}, size:9 };
                row.getCell(3).alignment = { horizontal:'center', vertical:'middle' };
                // KCS flag
                if (isKCS) {
                    row.getCell(9).font = { bold:true, color:{argb:'FFEF4444'}, size:9 };
                    row.getCell(9).alignment = { horizontal:'center' };
                }
            });
        }

        // Auto-filter on header row
        ws.autoFilter = {
            from: { row: meHdr.number, column: 1 },
            to:   { row: meHdr.number, column: 9 }
        };

        // Freeze panes — keep header and clause info visible
        ws.views = [{ state:'frozen', xSplit:0, ySplit: meHdr.number }];
    });

    // Generate and save
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    const filename = `QoC_Assessment_${facility.business_name.replace(/\s+/g,'_')}_${moment(assessment.assessment_date).format('YYYY-MM-DD')}.xlsx`;
    saveAs(blob, filename);
    return filename;
}
